import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { api } from "../api";
import { caseKey, type CaseDetail } from "../caseApi";
import { CaseProgramsPanel } from "../components/CaseProgramsPanel";
import { CustomerPanel } from "../components/CustomerPanel";
import { MatchPanel } from "../components/MatchPanel";
import { NotesPanel } from "../components/NotesPanel";
import { WorksheetPanel } from "../components/WorksheetPanel";

/**
 * The call screen: customer on the left, notes and matching in the centre. Programs open in the layout's docked
 * right-hand panel (also fed by the top search box), so the notes stay visible next to them.
 */
export function CaseWorkspace() {
  const { id } = useParams({ strict: false }) as { id: string };
  const q = useQuery({ queryKey: caseKey(id), queryFn: () => api<CaseDetail>(`/api/cases/${id}/detail`) });
  if (q.isLoading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (q.error || !q.data) return <p role="alert" className="text-sm text-red-600">Could not open this case. It may not exist, or it is not yours to view.</p>;
  const d = q.data;
  const closed = d.case.status === "closed";
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-semibold">{d.case.title}</h1>
        {closed && <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-semibold dark:bg-slate-700">Closed</span>}
      </div>
      <div className="grid gap-4 xl:grid-cols-[18rem_minmax(0,1fr)]">
        <CustomerPanel caseView={d.case} interactions={d.interactions} />
        <div className="space-y-4">
          {/* key: a refetch never replaces text the rep is typing; a different case starts clean */}
          <NotesPanel key={d.case.id} caseId={d.case.id} notes={d.notes} readOnly={closed} />
          <MatchPanel caseView={d.case} programs={d.programs} readOnly={closed} />
          <CaseProgramsPanel caseId={d.case.id} state={d.case.customer.stateCode} programs={d.programs} followups={d.followups} readOnly={closed} />
          <WorksheetPanel key={d.case.id} caseId={d.case.id} state={d.case.customer.stateCode} programs={d.programs} readOnly={closed} hasEmail={!!d.case.customer.email} />
        </div>
      </div>
    </div>
  );
}
